import { HttpError } from '@euler/lib/request/HttpError';
import { BiDatasetCatalog } from '@euler/model/bi/catalog';
import { BiDatasetRef, BiDatasetRefKind } from '@euler/model/bi/query';
import { useServiceFactory } from '@euler/services/factory';
import { useCallback, useEffect, useState } from 'react';
import { biErrorMessage } from '../../../functions/biErrors';
import { biSameDataset } from './biEditorLabels';
import { BiCatalogsState } from './useBiCatalogs';

export type BiDatasetCatalogState =
  | { kind: 'none' }
  | { kind: 'loading' }
  | { kind: 'ready'; catalog: BiDatasetCatalog }
  /** `retryable`: a read that failed, not a dataset no longer usable. */
  | { kind: 'failed'; message: string; retryable: boolean };

type UploadRead =
  | { datasetId: string; kind: 'loading' }
  | { datasetId: string; kind: 'ready'; catalog: BiDatasetCatalog }
  | {
      datasetId: string;
      kind: 'failed';
      message: string;
      retryable: boolean;
    };

/** The upload is gone or the caller may no longer use it. */
const kUnusable = new Set(['record_not_found', 'bi_dataset_permission_denied']);
const kUnusableMessage =
  '这个数据集已删除或你已不能使用：只能改标题和图表类型，或换一个数据集。';

/**
 * The catalog of the dataset a query is on: a built-in one from the list,
 * an uploaded one read by id (`GET /bi/datasets/:id`) when picked.
 */
export function useBiDatasetCatalog(
  dataset: BiDatasetRef | null,
  catalogs: BiCatalogsState,
): { state: BiDatasetCatalogState; retry: () => void } {
  const { biService } = useServiceFactory();
  const datasetId =
    dataset?.kind === BiDatasetRefKind.Upload ? dataset.datasetId : null;
  const [read, setRead] = useState<UploadRead | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!datasetId) return;
    let active = true;
    setRead({ datasetId, kind: 'loading' });
    biService
      .getDataset(datasetId)
      .then(detail => {
        if (!active) return;
        setRead(
          detail.kind === BiDatasetRefKind.Upload &&
            detail.dataset.id === datasetId
            ? { datasetId, kind: 'ready', catalog: detail.catalog }
            : {
                datasetId,
                kind: 'failed',
                message: kUnusableMessage,
                retryable: false,
              },
        );
      })
      .catch(error => {
        console.error('BI dataset catalog failed', error);
        if (!active) return;
        const unusable =
          error instanceof HttpError && kUnusable.has(error.subCode ?? '');
        setRead({
          datasetId,
          kind: 'failed',
          message: unusable
            ? kUnusableMessage
            : biErrorMessage(error, '这个数据集的字段暂时读不到。'),
          retryable: !unusable,
        });
      });
    return () => {
      active = false;
    };
  }, [biService, datasetId, attempt]);
  const retry = useCallback(() => setAttempt(value => value + 1), []);

  if (!dataset) return { state: { kind: 'none' }, retry };
  if (dataset.kind === BiDatasetRefKind.Sql) {
    if (catalogs.kind !== 'ready') return { state: { kind: 'loading' }, retry };
    const catalog = catalogs.builtin.find(entry =>
      biSameDataset(dataset, entry.dataset),
    );
    return {
      state: catalog
        ? { kind: 'ready', catalog }
        : {
            kind: 'failed',
            message:
              '你的角色不能使用这个数据集：只能改标题和图表类型，或换一个数据集。',
            retryable: false,
          },
      retry,
    };
  }
  if (!read || read.datasetId !== datasetId)
    return { state: { kind: 'loading' }, retry };
  switch (read.kind) {
    case 'loading':
      return { state: { kind: 'loading' }, retry };
    case 'ready':
      return { state: { kind: 'ready', catalog: read.catalog }, retry };
    case 'failed':
      return {
        state: {
          kind: 'failed',
          message: read.message,
          retryable: read.retryable,
        },
        retry,
      };
  }
}
